import { getConfig } from "../config";

export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  readonly extensions?: Record<string, unknown>;

  constructor(status: number, message: string, detail?: string, extensions?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.extensions = extensions;
  }
}

const PROBLEM_DETAILS_FIELDS = new Set(["type", "title", "status", "detail", "traceId", "errors", "extensions"]);

/**
 * Coleta as extensions do ProblemDetails. O ASP.NET Core aplaina as chaves de
 * `ProblemDetails.Extensions` no top-level do JSON (ex: `code`); também aceita
 * a forma aninhada em `extensions` por robustez.
 */
function parseProblemDetailsExtensions(data: Record<string, unknown>): Record<string, unknown> {
  const extensions: Record<string, unknown> = {};
  const nested = data.extensions;
  if (nested && typeof nested === "object") Object.assign(extensions, nested);
  for (const key of Object.keys(data)) {
    if (!PROBLEM_DETAILS_FIELDS.has(key)) extensions[key] = data[key];
  }
  return extensions;
}

interface ApiFetchOptions {
  baseUrl?: string;
  token?: string;
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Requisição HTTP à apogeu-api com header Authorization Bearer.
 * Usa `baseUrl` explícito ou o `BUN_PUBLIC_API_BASE_URL` do config
 * (vazio = same-origin, encaminhado pelo proxy do dev server).
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { baseUrl = getConfig().apiBaseUrl, token, method = "GET", body, headers: extraHeaders, signal } = options;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // FormData define seu próprio Content-Type (multipart + boundary); não sobrescrever.
  if (body !== undefined && !(body instanceof FormData)) headers["Content-Type"] = "application/json";
  if (extraHeaders) Object.assign(headers, extraHeaders);

  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    let detail: string | undefined;
    let extensions: Record<string, unknown> | undefined;
    try {
      const data = (await res.json()) as { detail?: string; title?: string; errors?: Record<string, string[]> } & Record<string, unknown>;
      const validationErrors = data.errors ? Object.values(data.errors).flat().filter(Boolean).join(" ") : undefined;
      detail = data.detail ?? validationErrors ?? data.title;
      extensions = parseProblemDetailsExtensions(data);
    } catch {
      // corpo não-JSON (ex: vazio)
    }
    throw new ApiError(res.status, res.statusText, detail, extensions);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
